/* ════════════════════════════════════════════════════════════════════
   Impression des rapports (gabarit « rapport-pro »).

   Différences avec impression() d'allfunction.js, laissée intacte pour
   les écrans qui en dépendent :
   - une seule feuille autonome (public/print/rapport-pro.css), sans CDN ;
   - vrai format A4, portrait ou paysage, posé par @page ;
   - impression déclenchée quand la feuille est réellement chargée
     (événement load), pas après un délai fixe ;
   - la fenêtre se referme d'elle-même après l'impression.
   ════════════════════════════════════════════════════════════════════ */

const echapper = (v) => String(v === undefined || v === null ? '' : v)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * En-tête standard d'un rapport.
 * @param {object} o { logo, societe, ifu, rccm, telephone, adresse, titre, sousTitre, periode, caisse, imprimePar, filtres }
 */
export function enteteRapport(o = {}) {
  const lignesIdent = [
    o.ifu || o.rccm ? `IFU : ${echapper(o.ifu)}${o.rccm ? ' &nbsp;·&nbsp; RCCM : ' + echapper(o.rccm) : ''}` : '',
    o.telephone ? `Tél. : ${echapper(o.telephone)}` : '',
    o.adresse ? echapper(o.adresse) : '',
  ].filter(Boolean).map(l => `<div class="rp-ident-l">${l}</div>`).join('');

  const maintenant = new Date();
  const horodatage = maintenant.toLocaleDateString('fr-FR') + ' à '
      + maintenant.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  const filtres = (o.filtres || []).filter(Boolean);

  return `
    <div class="rp-head">
      <div class="rp-ident">
        ${o.logo ? `<img class="rp-logo" src="${echapper(o.logo)}" alt="">` : ''}
        <div>
          <div class="rp-societe">${echapper(o.societe)}</div>
          ${lignesIdent}
        </div>
      </div>
      <div class="rp-titre">
        <h1>${echapper(o.titre)}</h1>
        ${o.sousTitre ? `<h2>${echapper(o.sousTitre)}</h2>` : ''}
        <div class="rp-meta">Période : <b>${echapper(o.periode)}</b></div>
        ${o.caisse ? `<div class="rp-meta">Caisse : <b>${echapper(o.caisse)}</b></div>` : ''}
        <div class="rp-meta">Imprimé par <b>${echapper(o.imprimePar)}</b> le ${horodatage}</div>
      </div>
    </div>
    ${filtres.length ? `<div class="rp-filtres">Filtres : ${filtres.map(echapper).join(' &nbsp;·&nbsp; ')}</div>` : ''}`;
}

/**
 * Ouvre la fenêtre d'impression et imprime.
 * @param {object} o { titre, html, orientation: 'portrait' | 'landscape' }
 * @returns {boolean} false si le navigateur a bloqué la fenêtre.
 */
export function imprimerRapport({ titre = 'Rapport', html = '', orientation = 'portrait' } = {}) {
  const base = process.env.VUE_APP_URL || '/';
  const feuille = base + 'print/rapport-pro.css';
  const largeur = orientation === 'landscape' ? 1100 : 820;

  const fen = window.open('', '', `width=${largeur},height=700`);
  if (!fen) return false;

  fen.document.open();
  fen.document.write(`<!doctype html>
<html lang="fr"><head>
<meta charset="utf-8">
<title>${echapper(titre)}</title>
<link rel="stylesheet" href="${feuille}">
<style>@page { size: A4 ${orientation === 'landscape' ? 'landscape' : 'portrait'}; }</style>
</head><body>
${html}
<scr` + `ipt>
  window.addEventListener('load', function () {
    window.onafterprint = function () { window.close(); };
    setTimeout(function () { window.focus(); window.print(); }, 150);
  });
</scr` + `ipt>
</body></html>`);
  fen.document.close();
  return true;
}
